import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { getChairImage, getChairSpecs } from '../data/chairs';
import './ChairDetail.css';

const ChairDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const image = getChairImage(id);
  const specs = getChairSpecs(t, id);
  const name = specs?.name || t(`chairs.${id}.name`, { defaultValue: id });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Unknown chair id
  if (!image && !specs) {
    return (
      <div className="chairdetail-page">
        <div className="chairdetail-container">
          <div className="chairdetail-notfound">
            <h2>{t('chairs.modal.noSpecs', { defaultValue: 'Chair not found.' })}</h2>
            <button className="chairdetail-back-btn" onClick={() => navigate('/conference-chairs')}>
              ← {t('menu.products')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const quoteHref = `https://wa.me/905408072727?text=${encodeURIComponent(
    `Merhaba, ${name} için teklif almak istiyorum.`
  )}`;

  return (
    <div className="chairdetail-page">
      <div className="chairdetail-container">
        {/* Breadcrumb / back */}
        <div className="chairdetail-top">
          <button className="chairdetail-back" onClick={() => navigate('/conference-chairs')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            {t('common.back', { defaultValue: 'Back' })}
          </button>
          <nav className="chairdetail-crumb">
            <Link to="/conference-chairs">{t('menu.products')}</Link>
            <span>/</span>
            <span className="current">{name}</span>
          </nav>
        </div>

        <motion.div
          className="chairdetail-grid"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Image */}
          <div className="chairdetail-media">
            <div className="chairdetail-image-card">
              {image && <img src={image} alt={name} />}
            </div>
          </div>

          {/* Info */}
          <div className="chairdetail-info">
            <span className="chairdetail-eyebrow">{t('chairs.modal.category', { defaultValue: 'Conference Seating' })}</span>
            <h1 className="chairdetail-title">{name}</h1>

            {specs ? (
              <>
                <p className="chairdetail-desc">{specs.description}</p>

                {(specs.dimensions.height || specs.dimensions.width || specs.dimensions.depth) && (
                  <section className="chairdetail-section">
                    <h3 className="chairdetail-section-title">{t('chairs.modal.dimensions')}</h3>
                    <div className="chairdetail-dims">
                      {specs.dimensions.height && (
                        <div className="chairdetail-dim">
                          <span className="chairdetail-dim-label">{t('chairs.modal.overallHeight')}</span>
                          <span className="chairdetail-dim-value">{specs.dimensions.height}</span>
                        </div>
                      )}
                      {specs.dimensions.width && (
                        <div className="chairdetail-dim">
                          <span className="chairdetail-dim-label">{t('chairs.modal.seatWidth')}</span>
                          <span className="chairdetail-dim-value">{specs.dimensions.width}</span>
                        </div>
                      )}
                      {specs.dimensions.depth && (
                        <div className="chairdetail-dim">
                          <span className="chairdetail-dim-label">{t('chairs.modal.seatDepth')}</span>
                          <span className="chairdetail-dim-value">{specs.dimensions.depth}</span>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                <section className="chairdetail-section">
                  <h3 className="chairdetail-section-title">{t('chairs.modal.technicalSpecs')}</h3>
                  <div className="chairdetail-specs">
                    {([
                      ['structure', specs.technical.structure],
                      ['foam', specs.technical.foam],
                      ['upholstery', specs.technical.upholstery],
                      ['armrests', specs.technical.armrests],
                      ['legStructure', specs.technical.legStructure],
                      ['seatingMechanism', specs.technical.seatingMechanism],
                    ] as const)
                      .filter(([, val]) => val)
                      .map(([key, val]) => (
                        <div className="chairdetail-spec" key={key}>
                          <span className="chairdetail-spec-label">{t(`chairs.modal.${key}`)}</span>
                          <span className="chairdetail-spec-value">{val}</span>
                        </div>
                      ))}
                  </div>
                </section>

                {specs.optional.length > 0 && (
                  <section className="chairdetail-section">
                    <h3 className="chairdetail-section-title">{t('chairs.modal.optionalFeatures')}</h3>
                    <ul className="chairdetail-list">
                      {specs.optional.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </section>
                )}

                {specs.applications.length > 0 && (
                  <section className="chairdetail-section">
                    <h3 className="chairdetail-section-title">{t('chairs.modal.applications')}</h3>
                    <ul className="chairdetail-list">
                      {specs.applications.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </section>
                )}

                {specs.warranty && (
                  <section className="chairdetail-section">
                    <h3 className="chairdetail-section-title">{t('chairs.modal.warranty')}</h3>
                    <p className="chairdetail-warranty">{specs.warranty}</p>
                  </section>
                )}
              </>
            ) : (
              <div className="chairdetail-nospecs">
                <p>{t('chairs.modal.noSpecs')}</p>
              </div>
            )}

            <div className="chairdetail-actions">
              <a className="chairdetail-quote" href={quoteHref} target="_blank" rel="noopener noreferrer">
                {t('chairs.modal.getQuote', { defaultValue: t('common.getQuote') })}
              </a>
              <button className="chairdetail-back-btn" onClick={() => navigate('/conference-chairs')}>
                ← {t('menu.products')}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ChairDetail;
